import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import {
	type IncomingInvoiceDeliveryNotePickSchema,
	incomingInvoiceDeliveryNotePickSchema,
} from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { IncomingInvoiceDeliveryNotePickForm } from "@/forms/incoming-invoice-delivery-note-pick-form";
import { client } from "@/hono";

/** Props Interface */
interface LinkDeliveryNoteModalProps extends ModalProps {
	incomingInvoiceId: string;
}

/** Link a waiting delivery note to this invoice, from the invoice's own page. */
export function LinkDeliveryNoteModal({
	isOpen,
	onClose,
	incomingInvoiceId,
}: LinkDeliveryNoteModalProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const methods = useForm<IncomingInvoiceDeliveryNotePickSchema>({
		defaultValues: { deliveryNoteId: "" },
		resolver: resolver(incomingInvoiceDeliveryNotePickSchema),
	});

	/** Functions */
	async function handleOnSubmit({ deliveryNoteId }: IncomingInvoiceDeliveryNotePickSchema) {
		const response = await client.incoming_invoices["delivery-notes"][":deliveryNoteId"].$patch({
			param: { deliveryNoteId },
			json: { incomingInvoiceId },
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["incoming-invoices"]) });
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("incoming_invoices.delivery_notes.link_delivery_note")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
			>
				<Modal.Body>
					<p className="label">{t("incoming_invoices.delivery_notes.link_delivery_note_hint")}</p>
					<IncomingInvoiceDeliveryNotePickForm />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
